import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid2,
  TextField,
  Button,
  Chip,
  Stack,
  Modal,
  Alert,
  Snackbar,
  Divider,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import ModalBox from "../../themes/overrides/ModalBox";
import { updatePrompt, getpromptByID } from "../../actions/promptStoreAction";
import AiModelSelector from "./AiModelSelector";
import { generateAIOutput } from "./AiGenerator";

const EditPromptForm = ({
  isOpen,
  onClose,
  promptId,
  onSuccess,
  workspaceId,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { register, handleSubmit, reset, setValue, watch } = useForm();

  const [variables, setVariables] = useState([]);
  const [variableInput, setVariableInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [message, setMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [selectedModel, setSelectedModel] = useState("");
  const [output, setOutput] = useState("");
  const [variableValues, setVariableValues] = useState({});
  const [testLoading, setTestLoading] = useState(false);

  const prompt = useSelector((state) => state.prompts.selectedPrompt);
  const promptTemplate = watch("promptTemplate", "");
  const systemInstruction = watch("systemInstruction");

  useEffect(() => {
    const matches = promptTemplate.match(/{{(.*?)}}/g);
    if (matches) {
      const cleaned = matches.map((m) => m.replace(/{{|}}/g, "").trim());
      setVariables([...new Set(cleaned)]); // remove duplicates
    } else {
      setVariables([]);
    }
  }, [promptTemplate]);

  useEffect(() => {
    if (promptId && isOpen) {
      dispatch(getpromptByID(workspaceId, promptId));
    }
  }, [promptId, isOpen, dispatch, workspaceId]);

  useEffect(() => {
    if (prompt && Object.keys(prompt).length > 0) {
      setValue("promptName", prompt.promptName || "");
      setValue("category", prompt.category || "");
      setValue("systemInstruction", prompt.systemInstruction || "");
      setValue("promptTemplate", prompt.promptTemplate || "");
      setVariables(prompt.variables || []);
    }
  }, [prompt, setValue]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      // const formData = { workspaceId, ...data, variables, id: promptId };
      const formData = { ...data, variables };

      const res = await dispatch(updatePrompt(formData, promptId));
      console.log(res);
      if (res?.status === "success") {
        setSeverity("success");
        setMessage("Prompt updated successfully!");
        setOpenSnackbar(true);
        if (onSuccess) onSuccess(res);
        if (onClose) onClose();
        navigate(`/${workspaceId}/prompt-library`);
      } else {
        setSeverity("error");
        setMessage(res?.message || "Failed to update prompt.");
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("Update error:", error);
      setSeverity("error");
      setMessage("An error occurred while updating the prompt.");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVariable = () => {
    if (variableInput && !variables.includes(variableInput)) {
      setVariables((prev) => [...prev, variableInput]);
      setVariableInput("");
    }
  };

  const handleDeleteVariable = (variableToDelete) => {
    setVariables((prev) => prev.filter((v) => v !== variableToDelete));
  };

  const handleModelSelect = (model) => {
    setSelectedModel(model);
  };

  const handleVariableChange = (key, value) => {
    setVariableValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleTestRun = async () => {
    try {
      if (!selectedModel) {
        setSeverity("error");
        setMessage("Please select an AI model first.");
        setOpenSnackbar(true);
        return;
      }

      if (!promptTemplate) {
        setSeverity("error");
        setMessage("Please enter a prompt template first.");
        setOpenSnackbar(true);
        return;
      }

      setTestLoading(true);
      let filledPrompt = promptTemplate;
      variables.forEach((v) => {
        const value = variableValues[v] || "";
        filledPrompt = filledPrompt.replaceAll(`{{${v}}}`, value);
      });

      filledPrompt = systemInstruction + ". " + filledPrompt;

      console.log(filledPrompt);
      const result = await generateAIOutput(
        selectedModel,
        filledPrompt,
        (streamChunk) => {
          setOutput(streamChunk); // updates live
        }
      );

      console.log("Final Output:", result);
    } catch (error) {
      console.error(error);
      setOutput("Error during generation.");
    } finally {
      setTestLoading(false);
    }
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  const handleClose = () => {
    reset();
    onClose();
    setOutput("");
  };

  return (
    <>
      <Modal open={isOpen} onClose={handleClose}>
        <ModalBox
          sx={{
            width: { xs: "95%", sm: "600px", md: "700px" },
            maxHeight: { xs: "95vh", sm: "90vh" },
          }}
        >
          <Typography variant="h2" sx={{ marginBottom: 2 }}>
            Edit Prompt
          </Typography>

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid2 container spacing={2}>
              <Grid2 size={12}>
                <TextField
                  label="Prompt Name"
                  fullWidth
                  required
                  {...register("promptName", { required: true })}
                  placeholder="e.g., Blog Post Generator"
                />
              </Grid2>

              <Grid2 size={12}>
                <TextField
                  label="Category"
                  fullWidth
                  {...register("category")}
                  placeholder="e.g., Marketing, Coding, Creative"
                />
              </Grid2>
            </Grid2>

            <Box mt={3}>
              <TextField
                label="System Instruction (LLM Persona)"
                fullWidth
                multiline
                rows={2}
                {...register("systemInstruction")}
                placeholder="e.g., Act as a senior technical writer who is concise and formal."
              />
            </Box>

            <Box mt={3}>
              <TextField
                label="Prompt Template Text"
                fullWidth
                multiline
                rows={3}
                {...register("promptTemplate")}
                placeholder="Write a blog post about {{topic}} for a {{target_audience}}."
              />
            </Box>

            

            <Box mt={4}>
              <Typography variant="subtitle1" fontWeight={500} mb={1}>
                Detected Variables
              </Typography>
              {variables.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" spacing={1}>
                  {variables.map((v, i) => (
                    <Chip
                      key={i}
                      label={v}
                      color="secondary"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" mt={1}>
                  No variables detected. in your template.
                </Typography>
              )}
            </Box>

            {/* DIY Run Section */}
            <Box
              mt={4}
              variant="outlined"
              sx={{ p: 2, backgroundColor: "#e6f7fdff" }}
            >
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" fontWeight={500} mb={2}>
                DIY Test Run
              </Typography>

              <AiModelSelector
                selectedModel={selectedModel}
                onModelSelect={handleModelSelect}
              />
              {variables.length > 0 && (
                <Box mt={2}>
                  {variables.map((v) => (
                    <TextField
                      key={v}
                      label={`Value for ${v}`}
                      fullWidth
                      margin="dense"
                      value={variableValues[v] || ""}
                      onChange={(e) => handleVariableChange(v, e.target.value)}
                    />
                  ))}
                </Box>
              )}

              <Box mt={3}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleTestRun}
                  disabled={testLoading}
                >
                  {testLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Run Test"
                  )}
                </Button>
              </Box>
              {output && (
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 3,
                    p: 2,
                    backgroundColor: "#f9f9f9",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  <Typography variant="subtitle2" mb={1}>
                    Test Output:
                  </Typography>
                  <Typography variant="body2">{output}</Typography>
                </Paper>
              )}
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="flex-end"
              spacing={2}
              mt={5}
            >
              <Button variant="outlined" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? "Updating..." : "Update Prompt"}
              </Button>
            </Stack>
          </Box>
        </ModalBox>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditPromptForm;
